export const DEFAULT_LANGUAGE_SETTING = "English";
export const DEFAULT_PDF_LAYOUT_SETTING = "Single";

export const DEFAULT_CURRENCY = "$ - CAD";

export const DEFAULT_REPORT_TYPE = "PDF" as ReportFileType;

// Timezones eligible for the Ontario MTO report format
export const ONTARIO_TIMEZONES = ["America/Toronto"];

// routes for operating manual
export const FIRST_MANUAL_ROUTE = "publication-information";
export const LAST_MANUAL_ROUTE = "appendix-f";

export const stereoTargetStyleOptions = ["TitmusCircles", "SimpleShapes"];

// The two shapes an aniseikonia section can present. Half discs are the default; the bar pair
// is slender across the axis it is not judged on, so its unscaled extents differ per meridian.
export const aniseikoniaTargetStyleOptions = ["HalfDisc", "BarPair"];

// How many times the patient is asked to match the two halves on each meridian.
//
// One by default, and the number matters more than it looks. Every check the section makes on
// its own measurement is a comparison between settings: the interval around the number needs
// two, the hysteresis check needs one approach from each side, and the control probes are
// presented at the running estimate so they do not exist until a meridian has two settings
// behind it. At one setting the report carries a number with none of those, and says so.
//
// The ceiling is four because the schedule's approach order is a crossing design of exactly
// four -- two from each side, arranged so each runs once with each reference eye.
export const DEFAULT_ANISEIKONIA_SETTINGS_PER_MERIDIAN = 1;
export const MAXIMUM_ANISEIKONIA_SETTINGS_PER_MERIDIAN = 4;

export const aniseikoniaSettingsPerMeridianOptions = Array.from(
  { length: MAXIMUM_ANISEIKONIA_SETTINGS_PER_MERIDIAN },
  (_, index) => index + 1
);

// Each meridian is six presentations and about two minutes, so these toggles are the section's
// duration lever. At least one has to be on.
export const aniseikoniaMeridians = [
  "horizontal",
  "vertical",
  "overall",
] as const;

// The synoptophore runs its near tests at a fixed 33 cm
export const NEAR_TEST_DISTANCE_CM = 33;

// Tabs in a patient's exam list (ClinicExams)
export const EXAMS_TAB_ID = 0;
export const PROGRESSION_ANALYSES_TAB_ID = 1;

export const DEFAULT_DECIBEL_VALUE_SCREENING = 25;
export const DEFAULT_DECIBEL_VALUE_SUPERIOR_36 = 10;
export const DEFAULT_DECIBEL_VALUE_FULL_120 = 10;
export const DEFAULT_DECIBEL_VALUE_ESTERMAN = 10;
export const MIN_DECIBEL_VALUE = 8;
export const MAX_DECIBEL_VALUE = 32;

// for patient prescription axis
export const MIN_RX_AXIS_VALUE = 0;
export const MAX_RX_AXIS_VALUE = 180;

// for creating progression analyses
export const MIN_EXAM_SELECTION = 3;
export const MAX_EXAM_SELECTION = 10;

export const DEFAULT_GAZE_SPECS = {
  maxValue: 90,
  minValue: -90,
  centralValue: 3,
  totalValues: 183,
};

export const DEFAULT_SIM_ENVIRONMENT = "DiningDay";
// TODO: can remove this once all environments are enabled
export const ENABLED_SIM_ENVIRONMENTS = [
  DEFAULT_SIM_ENVIRONMENT,
  "DrivingNight",
];

export const VISUAL_FIELD_TYPE = "VisualField";
export const VISUAL_FIELD_PATH = "visual-field";

export const OCT_TYPE = "OCT";

export const DEFAULT_STARTING_ROW_INDEX = 7;
export const visualAcuityRowOptions = [
  "20/200 ft, 6/60 m",
  "20/160 ft, 6/48 m",
  "20/125 ft, 6/38 m",
  "20/100 ft, 6/30 m",
  "20/80 ft, 6/24 m",
  "20/63 ft, 6/18 m",
  "20/50 ft, 6/15 m",
  "20/40 ft, 6/12 m",
  "20/32 ft, 6/9.5 m",
  "20/25 ft, 6/7.5 m",
  "20/20 ft, 6/6 m",
  "20/16 ft, 6/4.8 m",
  "20/12.5 ft, 6/3.8 m",
  "20/10 ft, 6/3 m",
];

export const tintOptions = {
  DEFAULT_GREY: {
    label: "Default (Grey)",
    value: "#202020bf",
  },
  AMBER: {
    label: "Amber",
    value: "#ffbf0033",
  },
  BROWN: {
    label: "Brown",
    value: "#32302fbf",
  },
  CINNAMON: {
    label: "Cinnamon",
    value: "#d1a89633",
  },
  GREEN: {
    label: "Green",
    value: "#228b2233",
  },
  LAVENDER: {
    label: "Lavender",
    value: "#a883c433",
  },
  PINK: {
    label: "Pink",
    value: "#ffc0cb33",
  },
  CUSTOM: {
    label: "Custom",
    value: "",
  },
};

// translate the label and translatedType fields when using
export const examCardEnums = {
  VISUAL_FIELD_SCREENING: {
    label: "Visual Field (Screening)",
    type: "VisualField_Screening",
    abbrevType: "VF",
    translatedType: "Visual Field",
    sectionName: "visualFieldScreeningSections",
    algorithmPath: "visual-field-screening",
  },
  VISUAL_FIELD_THRESHOLDING: {
    label: "Visual Field (Threshold)",
    type: "VisualField_Thresholding",
    abbrevType: "VF",
    translatedType: "Visual Field",
    sectionName: "visualFieldThresholdingSections",
    algorithmPath: "visual-field-threshold",
  },
  VISUAL_FIELD_FULL_120: {
    label: "Full 120",
    type: "VisualField_Full120",
    abbrevType: "VF",
    translatedType: "Visual Field",
    sectionName: "visualFieldFull120Sections",
    algorithmPath: "full-120",
  },
  VISUAL_FIELD_ESTERMAN: {
    label: "Esterman",
    type: "VisualField_Esterman",
    abbrevType: "VF",
    translatedType: "Visual Field",
    sectionName: "visualFieldEstermanSections",
    algorithmPath: "esterman",
  },
  VISUAL_FIELD_SUPERIOR_36: {
    label: "Superior 36",
    type: "VisualField_Superior36",
    abbrevType: "VF",
    translatedType: "Visual Field",
    sectionName: "visualFieldSuperior36Sections",
    algorithmPath: "superior-36",
  },
  COLOR_VISION: {
    label: "Color Vision",
    type: "ColorVision",
    abbrevType: "CV",
    translatedType: "Color Vision",
    sectionName: "colorVisionSections",
    algorithmPath: "color-vision",
  },
  CONTRAST_SENSITIVITY: {
    label: "Contrast Sensitivity",
    type: "ContrastSensitivity",
    abbrevType: "CS",
    translatedType: "Contrast Sensitivity",
    sectionName: "contrastSensitivitySections",
    algorithmPath: "contrast-sensitivity",
  },
  VISUAL_ACUITY: {
    label: "Visual Acuity",
    type: "VisualAcuity",
    abbrevType: "VA",
    translatedType: "Visual Acuity",
    sectionName: "visualAcuitySections",
    algorithmPath: "visual-acuity",
  },
  DEVIATION_AND_CORRESPONDENCE: {
    label: "Deviation and Correspondence - Beta",
    type: "Synoptophore_Deviation",
    abbrevType: "SY",
    translatedType: "Synoptophore",
    sectionName: "deviationAndCorrespondenceSections",
    algorithmPath: "deviation-and-correspondence",
  },
  FUSION_AND_VERGENCE: {
    label: "Fusion and Vergence - Beta",
    type: "Synoptophore_Fusion",
    abbrevType: "SY",
    translatedType: "Synoptophore",
    sectionName: "fusionAndVergenceSections",
    algorithmPath: "fusion-and-vergence",
  },
  STEREOPSIS: {
    label: "Stereopsis - Beta",
    type: "Synoptophore_Stereopsis",
    abbrevType: "SY",
    translatedType: "Synoptophore",
    sectionName: "stereopsisSections",
    algorithmPath: "stereopsis",
  },
  ANISEIKONIA: {
    label: "Aniseikonia - Beta",
    type: "Aniseikonia",
    abbrevType: "AN",
    translatedType: "Aniseikonia",
    sectionName: "aniseikoniaSections",
    algorithmPath: "aniseikonia",
  },
  RAPD: {
    label: "RAPD",
    type: "RAPD",
    abbrevType: "RAPD",
    translatedType: "RAPD",
    sectionName: "rapdSections",
    algorithmPath: "rapd",
  },
  OCULAR_MOTILITY: {
    label: "Ocular Motility",
    type: "OcularMotility",
    abbrevType: "OM",
    translatedType: "Ocular Motility",
    sectionName: "ocularMotilitySections",
    algorithmPath: "ocular-motility",
  },
  IOL_SIMULATION: {
    label: "Lens Simulation",
    type: "LensSimulation_IOL",
    abbrevType: "LS",
    translatedType: "Lens Simulation",
    sectionName: "iolLensSimulationSections",
    algorithmPath: "iol-lens-simulation",
  },
  PROGRESSIVE_SIMULATION: {
    label: "Lens Simulation",
    type: "LensSimulation_Progressive",
    abbrevType: "LS",
    translatedType: "Lens Simulation",
    sectionName: "progressiveLensSimulationSections",
    algorithmPath: "progressive-lens-simulation",
  },
  VIDEO: {
    label: "Video",
    type: "Video",
    abbrevType: "V",
    translatedType: "Video",
    sectionName: "videoSections",
    algorithmPath: "video",
  },
} as const;

export type ExamCardSectionType =
  (typeof examCardEnums)[keyof typeof examCardEnums]["type"];

export type ExamCardSectionNameType =
  (typeof examCardEnums)[keyof typeof examCardEnums]["sectionName"];

export type DefaultExamCardType =
  (typeof examCardEnums)[keyof typeof examCardEnums];

export const speedTypeEnums = {
  NORMAL: "Normal",
  SLOW: "Slow",
};

export const gridTypeEnums = {
  G_10_2: { type: "G_10_2", label: "10-2" },
  G_24_2: { type: "G_24_2", label: "24-2" },
  G_24_2_C: { type: "G_24_2_C", label: "24-2C" },
  G_30_2: { type: "G_30_2", label: "30-2" },
  G_C_20: { type: "G_C_20", label: "C20" },
  G_C_40: { type: "G_C_40", label: "C40" },
  G_120: { type: "G_120", label: "120" },
  G_120_M: { type: "G_120_M", label: "120M" }, // Esterman monocular
  FULL_120: { type: "FULL_120", label: "120" },
};

export const eyeTypeEnums = {
  LEFT: "Left",
  RIGHT: "Right",
  BOTH: "Both",
};

export const fixationLossMethodEnums = {
  EYE_TRACKING: "EyeTracking",
  BLIND_SPOT: "BlindSpot",
};

export const fixationPointShapeEnums = {
  SPHERE: "Sphere",
  DIAMOND: "Diamond",
};

export const positionTypeEnums = {
  NEAR: "Near",
  FAR: "Far",
};

export const stimulusTypeEnums = {
  III: "III",
  IV: "IV",
  V: "V",
};

export const stimulusColorEnums = {
  RED: "Red",
  WHITE: "White",
};

// The platform an exam came from. Our own device is one value among the external
// machines, so telling them apart is a tag comparison rather than a type distinction.
export const examSourceEnums = {
  RETINALOGIK: "RetinaLogik",
};

export const sexTypeEnums = {
  MALE: "Male",
  FEMALE: "Female",
  UNDEFINED: "Other",
};

export const DeviceStagesTimeline = {
  Invalid: undefined,
  Undefined: undefined,
  PatientLogin: 0,
  Confirmation: 1,
  Calibration: 2,
  Tutorial: 3,
  Exam: 4,
  Completed: 5,
  Terminated: 6,
};

export const ReportStatus = {
  Unavailable: "Unavailable",
  Pending: "Pending",
  InProgress: "InProgress",
  Ready: "Ready",
  Failed: "Failed",
  Skipped: "Skipped",
};

export const reportFileTypes = ["PDF", "DICOM", "PNG"] as const;
export type ReportFileType = (typeof reportFileTypes)[number];

export const colorVisionDeficiencyTypes = [
  "NormalVision",
  "RedGreenDeficiency",
  "Protanopia",
  "Deuteranopia",
  "Undefined",
] as const;
export type ColorVisionDeficiencyType =
  (typeof colorVisionDeficiencyTypes)[number];

export const patientConditionTypes = [
  "Glaucoma",
  "AMD",
  "DiabeticRetinopathy",
  "NeuroCondition",
  "MacularCondition",
] as const;
export type PatientConditionType = (typeof patientConditionTypes)[number];

export const DEFAULT_COLOR_VISION_CARD = {
  examFieldType: examCardEnums.COLOR_VISION.algorithmPath,
  examType: "D_15",
  eyeCheckboxes: { right: true, left: true },
  binocular: true,
  sectionOrder: { right: 0, left: 1 },
  optionalToggles: {
    requiresTutorial: true,
    unlimitedTime: true,
  },
};

export const DEFAULT_FULL120_CARD = {
  examFieldType: examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath,
  examType: "FULL_120",
  decibelValue: DEFAULT_DECIBEL_VALUE_FULL_120,
  eyeCheckboxes: { right: true, left: true },
  visualGrid: gridTypeEnums.G_120.type,
  stimulusSizeType: stimulusTypeEnums.III,
  speedType: speedTypeEnums.NORMAL,
  fovea: false,
  fixationType: {
    fixationPointShapeType: fixationPointShapeEnums.SPHERE,
    fixationLossMethod: fixationLossMethodEnums.EYE_TRACKING,
  },
  sectionOrder: { right: 0, left: 1 },
  optionalToggles: {
    autoPause: true,
    calibration: false,
    encouragement: true,
    requiresTutorial: true,
    showProgressRing: true,
  },
};

export const DEFAULT_ESTERMAN_CARD = {
  examFieldType: examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath,
  examType: "ESTERMAN_FAST",
  decibelValue: DEFAULT_DECIBEL_VALUE_ESTERMAN,
  eyeCheckboxes: { right: true, left: true },
  visualGrid: gridTypeEnums.G_120.type,
  speedType: speedTypeEnums.NORMAL,
  fixationType: {
    fixationPointShapeType: fixationPointShapeEnums.SPHERE,
    fixationLossMethod: fixationLossMethodEnums.EYE_TRACKING,
  },
  sectionOrder: { right: 0, left: 1 },
  binocular: true,
  fovea: false,
  optionalToggles: {
    autoPause: true,
    calibration: false,
    encouragement: true,
    requiresTutorial: true,
    showProgressRing: true,
  },
};

export const DEFAULT_VF_SCREENING_CARD = {
  examFieldType: examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath,
  examType: "SCREEN_FAST",
  decibelValue: DEFAULT_DECIBEL_VALUE_SCREENING,
  eyeCheckboxes: { right: true, left: true },
  visualGrid: gridTypeEnums.G_C_20.type,
  stimulusSizeType: stimulusTypeEnums.III,
  stimulusColor: stimulusColorEnums.WHITE,
  speedType: speedTypeEnums.NORMAL,
  fixationType: {
    fixationPointShapeType: fixationPointShapeEnums.SPHERE,
    fixationLossMethod: fixationLossMethodEnums.EYE_TRACKING,
  },
  fovea: false,
  sectionOrder: { right: 0, left: 1 },
  optionalToggles: {
    autoPause: true,
    calibration: false,
    encouragement: true,
    requiresTutorial: true,
    showProgressRing: true,
  },
};

export const DEFAULT_VF_THRESHOLDING_CARD = {
  examFieldType: examCardEnums.VISUAL_FIELD_THRESHOLDING.algorithmPath,
  examType: "FAST_THRESHOLD",
  eyeCheckboxes: { right: true, left: true },
  visualGrid: gridTypeEnums.G_24_2.type,
  stimulusSizeType: stimulusTypeEnums.III,
  stimulusColor: stimulusColorEnums.WHITE,
  speedType: speedTypeEnums.NORMAL,
  fixationType: {
    fixationPointShapeType: fixationPointShapeEnums.SPHERE,
    fixationLossMethod: fixationLossMethodEnums.EYE_TRACKING,
  },
  fovea: false,
  sectionOrder: { right: 0, left: 1 },
  optionalToggles: {
    autoPause: true,
    calibration: false,
    encouragement: true,
    requiresTutorial: true,
    showProgressRing: true,
  },
};

export const DEFAULT_CONTRAST_SENSITIVITY_CARD = {
  examFieldType: examCardEnums.CONTRAST_SENSITIVITY.algorithmPath,
  examType: "PELLI_ROBSON",
  eyeCheckboxes: { right: true, left: true },
  binocular: true,
  sectionOrder: { right: 0, left: 1 },
  optionalToggles: {
    requiresTutorial: true,
  },
};

export const DEFAULT_VISUAL_ACUITY_CARD = {
  examFieldType: examCardEnums.VISUAL_ACUITY.algorithmPath,
  examType: "SNELLEN",
  startingRowIndex: DEFAULT_STARTING_ROW_INDEX,
  eyeCheckboxes: { right: true, left: true },
  position: positionTypeEnums.FAR,
  binocular: false,
  sectionOrder: { right: 0, left: 1 },
  optionalToggles: {
    requiresTutorial: true,
  },
};

export const DEFAULT_DEVIATION_AND_CORRESPONDENCE_CARD = {
  examFieldType: examCardEnums.DEVIATION_AND_CORRESPONDENCE.algorithmPath,
  examType: "DEVIATION_AND_CORRESPONDENCE",
  eyeCheckboxes: { right: true, left: true },
  binocular: true,
  objectiveAlignment: true,
  subjectiveAngle: false,
  cyclodeviation: false,
  optionalToggles: {},
};

export const DEFAULT_FUSION_AND_VERGENCE_CARD = {
  examFieldType: examCardEnums.FUSION_AND_VERGENCE.algorithmPath,
  examType: "FUSION_AND_VERGENCE",
  eyeCheckboxes: { right: true, left: true },
  binocular: true,
  fusionalAmplitudes: false,
  vergenceFunction: true,
  optionalToggles: {},
};

export const DEFAULT_STEREOPSIS_CARD = {
  examFieldType: examCardEnums.STEREOPSIS.algorithmPath,
  examType: "STEREOPSIS",
  eyeCheckboxes: { right: true, left: true },
  binocular: true,
  stereoTargetStyle: stereoTargetStyleOptions[0],
  optionalToggles: {},
};

// `calibration` and `requiresTutorial` are deliberately absent. The webservice reads neither:
// calibration is always stored true because the section places its fusion lock at the angle the
// eye tracker says the patient fuses at, and the tutorial flag is always stored false because the
// section's instructions are a stage of the section rather than something played in front of it.
export const DEFAULT_ANISEIKONIA_CARD = {
  examFieldType: examCardEnums.ANISEIKONIA.algorithmPath,
  examType: "DIRECT_COMPARISON",
  eyeCheckboxes: { right: true, left: true },
  binocular: true,
  position: positionTypeEnums.FAR,
  horizontal: true,
  vertical: true,
  overall: true,
  aniseikoniaTargetStyle: aniseikoniaTargetStyleOptions[0],
  aniseikoniaSettingsPerMeridian: DEFAULT_ANISEIKONIA_SETTINGS_PER_MERIDIAN,
  optionalToggles: {},
};

export const DEFAULT_RAPD_CARD = {
  examFieldType: examCardEnums.RAPD.algorithmPath,
  examType: "THRESHOLDING",
  eyeCheckboxes: { right: true, left: true },
  binocular: true,
  optionalToggles: {
    calibration: true,
    requiresTutorial: true,
  },
};

export const DEFAULT_MOTILITY_CARD = {
  examFieldType: examCardEnums.OCULAR_MOTILITY.algorithmPath,
  examType: "H_PATTERN",
  eyeCheckboxes: { right: true, left: true },
  binocular: true,
  optionalToggles: {
    calibration: true,
    requiresTutorial: true,
  },
};

export const DEFAULT_VIDEO_CARD = {
  examFieldType: examCardEnums.VIDEO.algorithmPath,
  examType: "VIDEO",
  videoId: "",
  videoName: "",
  binocular: true,
  sectionOrder: { right: 0, left: 1 },
  eyeCheckboxes: { right: true, left: true },
  optionalToggles: {},
};

export const DEFAULT_IOL_SIMULATION_CARD = {
  examFieldType: examCardEnums.IOL_SIMULATION.algorithmPath,
  examType: "IOL_LENS_SIMULATION",
  simulationEnvironmentType: "",
  lensIds: [],
  binocular: true,
  sectionOrder: { right: 0, left: 1 },
  eyeCheckboxes: { right: true, left: true },
  optionalToggles: {},
};

export const DEFAULT_PROGRESSIVE_SIMULATION_CARD = {
  examFieldType: examCardEnums.PROGRESSIVE_SIMULATION.algorithmPath,
  examType: "PROGRESSIVE_LENS_SIMULATION",
  simulationEnvironmentType: "",
  lensIds: [],
  binocular: true,
  sectionOrder: { right: 0, left: 1 },
  eyeCheckboxes: { right: true, left: true },
  optionalToggles: {},
};

export const DEFAULT_SUPERIOR_36_CARD = {
  examFieldType: examCardEnums.VISUAL_FIELD_SUPERIOR_36.algorithmPath,
  examType: "SUPERIOR_36",
  decibelValue: DEFAULT_DECIBEL_VALUE_SUPERIOR_36,
  eyeCheckboxes: { right: true, left: true },
  visualGrid: gridTypeEnums.G_120.type,
  stimulusSizeType: stimulusTypeEnums.III,
  speedType: speedTypeEnums.NORMAL,
  fovea: false,
  fixationType: {
    fixationPointShapeType: fixationPointShapeEnums.SPHERE,
    fixationLossMethod: fixationLossMethodEnums.EYE_TRACKING,
  },
  sectionOrder: { right: 0, left: 1 },
  optionalToggles: {
    autoPause: true,
    calibration: false,
    encouragement: true,
    requiresTutorial: true,
    showProgressRing: true,
  },
};

export const DEFAULT_EXAM_CARDS = [
  DEFAULT_COLOR_VISION_CARD,
  DEFAULT_CONTRAST_SENSITIVITY_CARD,
  DEFAULT_FULL120_CARD,
  DEFAULT_SUPERIOR_36_CARD,
  DEFAULT_RAPD_CARD,
  DEFAULT_VISUAL_ACUITY_CARD,
  DEFAULT_VF_SCREENING_CARD,
  DEFAULT_VF_THRESHOLDING_CARD,
  DEFAULT_ESTERMAN_CARD,
  DEFAULT_MOTILITY_CARD,
  DEFAULT_IOL_SIMULATION_CARD,
  DEFAULT_PROGRESSIVE_SIMULATION_CARD,
  DEFAULT_VIDEO_CARD,
  DEFAULT_DEVIATION_AND_CORRESPONDENCE_CARD,
  DEFAULT_FUSION_AND_VERGENCE_CARD,
  DEFAULT_STEREOPSIS_CARD,
  DEFAULT_ANISEIKONIA_CARD,
];

export const PROGRESSION_CHART_SHAPES = {
  FAST_THRESHOLD: "circle",
  STANDARD_THRESHOLD: "triangle",
  FULL_THRESHOLD: "square",
};

export const algorithmTypes = {
  SCREEN_FAST: "SCREEN_FAST",
  FAST_THRESHOLD: "FAST_THRESHOLD",
  STANDARD_THRESHOLD: "STANDARD_THRESHOLD",
  FULL_THRESHOLD: "FULL_THRESHOLD",
  ESTERMAN_FAST: "ESTERMAN_FAST",
  FULL_120: "FULL_120",
  PELLI_ROBSON: "PELLI_ROBSON",
  TUMBLING_E: "TUMBLING_E",
  LANDOLT_C: "LANDOLT_C",
  SNELLEN: "SNELLEN",
  BAILEY_LOVIE: "BAILEY_LOVIE",
  BINOCULAR_VISION: "BINOCULAR_VISION",
  H_PATTERN: "H_PATTERN",
  NINE_GAZE: "NINE_GAZE",
  ETDRS: "ETDRS",
  SUPERIOR_36: "SUPERIOR_36",
  DEVIATION_AND_CORRESPONDENCE: "DEVIATION_AND_CORRESPONDENCE",
  FUSION_AND_VERGENCE: "FUSION_AND_VERGENCE",
  STEREOPSIS: "STEREOPSIS",
  DIRECT_COMPARISON: "DIRECT_COMPARISON",
  IOL_LENS_SIMULATION: "IOL_LENS_SIMULATION",
  PROGRESSIVE_LENS_SIMULATION: "PROGRESSIVE_LENS_SIMULATION",
  VIDEO: "VIDEO",
};

// GPA chart colors
export const GPA_CHART_COLORS = {
  unflagged: "#22c55e", // green
  flagged_once: "#eab308", // yellow
  flagged_twice: "#f97316", // orange
  flagged_multiple: "#ef4444", // red
  deep_loss: "#ef4444", // red (same as flagged_multiple)
};

export const CURRENCY_DATA = {
  CAD: { symbol: "$", label: "CAD" },
  USD: { symbol: "$", label: "USD" },
  NZD: { symbol: "$", label: "NZD" },
  ZAR: { symbol: "R", label: "ZAR" },
  AED: { symbol: "D", label: "AED" },
  GBP: { symbol: "£", label: "GBP" },
};

export type OcularMotilityPositionLayoutType = {
  key: string;
  label: string;
  subtitle: string;
  gridRow: number;
  gridColumn: number;
};

export const NINE_GAZE_POSITION_LAYOUT: Array<OcularMotilityPositionLayoutType> =
  [
    {
      key: "Primary",
      label: "Primary",
      subtitle: "straight ahead",
      gridRow: 1,
      gridColumn: 1,
    },
    {
      key: "Right",
      label: "Right",
      subtitle: "dextroversion",
      gridRow: 1,
      gridColumn: 2,
    },
    {
      key: "UpRight",
      label: "Up and Right",
      subtitle: "dextroelevation",
      gridRow: 1,
      gridColumn: 3,
    },
    {
      key: "Up",
      label: "Up",
      subtitle: "sursumversion",
      gridRow: 2,
      gridColumn: 1,
    },
    {
      key: "UpLeft",
      label: "Up and Left",
      subtitle: "laevoelevation",
      gridRow: 2,
      gridColumn: 2,
    },
    {
      key: "Left",
      label: "Left",
      subtitle: "laevoversion",
      gridRow: 2,
      gridColumn: 3,
    },

    {
      key: "DownLeft",
      label: "Down and Left",
      subtitle: "laevodepression",
      gridRow: 3,
      gridColumn: 1,
    },
    {
      key: "Down",
      label: "Down",
      subtitle: "deorsumversion",
      gridRow: 3,
      gridColumn: 2,
    },
    {
      key: "DownRight",
      label: "Down and Right",
      subtitle: "dextrodepression",
      gridRow: 3,
      gridColumn: 3,
    },
  ];

export const H_PATTERN_POSITION_LAYOUT: Array<OcularMotilityPositionLayoutType> =
  [
    {
      key: "1",
      label: "Primary",
      subtitle: "straight ahead",
      gridRow: 2,
      gridColumn: 1,
    },
    {
      key: "2",
      label: "Left",
      subtitle: "laevoversion",
      gridRow: 1,
      gridColumn: 3,
    },
    {
      key: "3",
      label: "Up and Left",
      subtitle: "laevoelevation",
      gridRow: 1,
      gridColumn: 1,
    },
    {
      key: "4",
      label: "Up and Right",
      subtitle: "dextroelevation",
      gridRow: 1,
      gridColumn: 2,
    },
    {
      key: "5",
      label: "Right",
      subtitle: "dextroversion",
      gridRow: 2,
      gridColumn: 2,
    },
    {
      key: "6",
      label: "Down and Right",
      subtitle: "dextrodepression",
      gridRow: 3,
      gridColumn: 1,
    },
    {
      key: "7",
      label: "Down and Left",
      subtitle: "laevodepression",
      gridRow: 2,
      gridColumn: 3,
    },
  ];
